import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { Car } from '../components/CarCard';
import { carsData as initialCars } from '../data/cars';
import { db, isFirebaseConfigured } from '../firebase';
import {
  collection, doc, setDoc, deleteDoc, onSnapshot,
  query, orderBy, getDocs, writeBatch, getDoc,
} from 'firebase/firestore';

interface CarsContextType {
  cars: Car[];
  addCar: (car: Omit<Car, 'id'>) => Promise<void>;
  updateCar: (id: string, car: Partial<Car>) => Promise<void>;
  deleteCar: (id: string) => Promise<void>;
  getCar: (id: string) => Car | undefined;
  reorderCars: (fromIndex: number, toIndex: number) => Promise<void>;
}

const LOCAL_STORAGE_KEY = 'eurojdm_cars';
const CARS_UPDATED_EVENT = 'eurojdm:cars-updated';
const CARS_COLLECTION = 'cars';

const normalizeCars = (input: unknown): Car[] => {
  if (!Array.isArray(input)) return [];

  return input
    .filter((item): item is Partial<Car> & { id: string; title: string } => (
      !!item &&
      typeof item === 'object' &&
      typeof (item as Partial<Car>).id === 'string' &&
      typeof (item as Partial<Car>).title === 'string'
    ))
    .map((item) => ({
      id: item.id,
      title: item.title,
      year: typeof item.year === 'number' ? item.year : new Date().getFullYear(),
      make: typeof item.make === 'string' ? item.make : '',
      model: typeof item.model === 'string' ? item.model : '',
      mileage: typeof item.mileage === 'string' ? item.mileage : '',
      price: typeof item.price === 'string' ? item.price : 'Contact Us',
      description: typeof item.description === 'string' ? item.description : '',
      features: Array.isArray(item.features)
        ? item.features.filter((f): f is string => typeof f === 'string')
        : [],
      issues: Array.isArray(item.issues)
        ? item.issues.filter((i): i is string => typeof i === 'string')
        : [],
      images: Array.isArray(item.images)
        ? item.images.filter((img): img is string => typeof img === 'string')
        : [],
      photoLink: typeof item.photoLink === 'string' ? item.photoLink : '',
      status: item.status === 'sold' ? 'sold' : 'available',
      datePosted: typeof item.datePosted === 'string'
        ? item.datePosted
        : new Date().toISOString().split('T')[0],
    }));
};

/* ── localStorage helpers (fallback when Firebase is not configured) ── */

const readCarsFromLocalStorage = (): Car[] => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!stored) return [];
  try { return normalizeCars(JSON.parse(stored)); } catch { return []; }
};

const saveCarsToLocalStorage = (cars: Car[]) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cars));
  window.dispatchEvent(new CustomEvent(CARS_UPDATED_EVENT));
};

const loadCarsFromLocalStorage = (): Car[] => {
  const storedCars = readCarsFromLocalStorage();
  if (storedCars.length > 0) {
    const storedIds = new Set(storedCars.map(c => c.id));
    const newSeedCars = initialCars.filter(c => !storedIds.has(c.id));
    if (newSeedCars.length > 0) {
      const merged = [...storedCars, ...newSeedCars];
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(merged));
      return merged;
    }
    return storedCars;
  }
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(initialCars));
  return initialCars;
};

/* ── Firebase helpers ── */

const seedFirestoreIfNeeded = async () => {
  if (!db) return;
  const metaRef = doc(db, '_meta', 'initialized');
  const metaSnap = await getDoc(metaRef);
  if (metaSnap.exists()) return;

  const batch = writeBatch(db);
  initialCars.forEach((car, index) => {
    batch.set(doc(db!, CARS_COLLECTION, car.id), { ...car, sortOrder: index });
  });
  batch.set(metaRef, { seeded: true, at: new Date().toISOString() });
  await batch.commit();
};

/* ── Context ── */

const CarsContext = createContext<CarsContextType | null>(null);

export const CarsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cars, setCars] = useState<Car[]>(loadCarsFromLocalStorage);

  const carsRef = useRef(cars);
  carsRef.current = cars;

  /* ── Firestore real-time sync ── */
  useEffect(() => {
    if (!isFirebaseConfigured || !db) return;

    seedFirestoreIfNeeded().catch(console.error);

    const q = query(collection(db, CARS_COLLECTION), orderBy('sortOrder'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firestoreCars: Car[] = snapshot.docs.map(d => {
        const { sortOrder, ...rest } = d.data();
        return rest as Car;
      });
      if (firestoreCars.length > 0) {
        setCars(firestoreCars);
        saveCarsToLocalStorage(firestoreCars);
      }
    }, (error) => {
      console.error('Firestore cars sync error:', error);
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── localStorage cross-tab sync (fallback mode only) ── */
  useEffect(() => {
    if (isFirebaseConfigured) return;

    const syncFromStorage = () => {
      setCars((current) => {
        const latest = readCarsFromLocalStorage();
        if (latest.length === 0) return current;
        return JSON.stringify(current) === JSON.stringify(latest) ? current : latest;
      });
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === LOCAL_STORAGE_KEY || event.key === null) syncFromStorage();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncFromStorage();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', syncFromStorage);
    window.addEventListener(CARS_UPDATED_EVENT, syncFromStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', syncFromStorage);
      window.removeEventListener(CARS_UPDATED_EVENT, syncFromStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  /* ── CRUD ── */

  const addCar = useCallback(async (car: Omit<Car, 'id'>) => {
    const newCar: Car = { ...car, id: Date.now().toString() };

    if (isFirebaseConfigured && db) {
      const snap = await getDocs(collection(db, CARS_COLLECTION));
      const minOrder = snap.docs.length > 0
        ? Math.min(...snap.docs.map(d => (d.data().sortOrder as number) ?? 0))
        : 0;
      await setDoc(doc(db, CARS_COLLECTION, newCar.id), {
        ...newCar, sortOrder: minOrder - 1,
      });
    } else {
      setCars(prev => {
        const updated = [newCar, ...prev];
        saveCarsToLocalStorage(updated);
        return updated;
      });
    }
  }, []);

  const updateCar = useCallback(async (id: string, updates: Partial<Car>) => {
    if (isFirebaseConfigured && db) {
      const carRef = doc(db, CARS_COLLECTION, id);
      const existing = await getDoc(carRef);
      if (existing.exists()) {
        await setDoc(carRef, { ...existing.data(), ...updates }, { merge: true });
      }
    } else {
      setCars(prev => {
        const updated = prev.map(car => car.id === id ? { ...car, ...updates } : car);
        saveCarsToLocalStorage(updated);
        return updated;
      });
    }
  }, []);

  const deleteCar = useCallback(async (id: string) => {
    if (isFirebaseConfigured && db) {
      await deleteDoc(doc(db, CARS_COLLECTION, id));
    } else {
      setCars(prev => {
        const updated = prev.filter(car => car.id !== id);
        saveCarsToLocalStorage(updated);
        return updated;
      });
    }
  }, []);

  const getCar = useCallback((id: string) => {
    return carsRef.current.find(car => car.id === id);
  }, []);

  const reorderCars = useCallback(async (fromIndex: number, toIndex: number) => {
    if (isFirebaseConfigured && db) {
      const current = [...carsRef.current];
      const [moved] = current.splice(fromIndex, 1);
      current.splice(toIndex, 0, moved);
      const batch = writeBatch(db);
      current.forEach((car, index) => {
        batch.update(doc(db!, CARS_COLLECTION, car.id), { sortOrder: index });
      });
      await batch.commit();
    } else {
      setCars(prev => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, moved);
        saveCarsToLocalStorage(updated);
        return updated;
      });
    }
  }, []);

  return (
    <CarsContext.Provider value={{ cars, addCar, updateCar, deleteCar, getCar, reorderCars }}>
      {children}
    </CarsContext.Provider>
  );
};

export const useCars = (): CarsContextType => {
  const context = useContext(CarsContext);
  if (!context) {
    throw new Error('useCars must be used within a CarsProvider');
  }
  return context;
};
